// src/components/BestDayWorstDay.jsx
import React, { useMemo } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import { useFinance } from "../contexts/FinanceContext";
import { useTranslation } from "react-i18next";

export default function BestDayWorstDay() {
  const { t } = useTranslation();
  const { history = [] } = useFinance();

  const result = useMemo(() => {
    if (!history || history.length === 0) return null;

    const incomeTotals = {};
    const spendingTotals = {};

    history.forEach((entry) => {
      const day = format(new Date(entry.date), "yyyy-MM-dd");

      if (entry.type === "income") {
        incomeTotals[day] = (incomeTotals[day] || 0) + Number(entry.amount);
      } else if (entry.type === "spending") {
        spendingTotals[day] = (spendingTotals[day] || 0) + Number(entry.amount);
      }
    });

    const incomeDays = Object.entries(incomeTotals).map(([date, amount]) => ({
      date,
      amount,
    }));

    const spendingDays = Object.entries(spendingTotals).map(
      ([date, amount]) => ({
        date,
        amount,
      })
    );

    if (incomeDays.length === 0 && spendingDays.length === 0) return null;

    const incomeBest =
      incomeDays.length > 0
        ? incomeDays.reduce((a, b) => (a.amount > b.amount ? a : b))
        : null;
    const incomeWorst =
      incomeDays.length > 0
        ? incomeDays.reduce((a, b) => (a.amount < b.amount ? a : b))
        : null;

    const spendingMost =
      spendingDays.length > 0
        ? spendingDays.reduce((a, b) => (a.amount > b.amount ? a : b))
        : null;
    const spendingLeast =
      spendingDays.length > 0
        ? spendingDays.reduce((a, b) => (a.amount < b.amount ? a : b))
        : null;

    return { incomeBest, incomeWorst, spendingMost, spendingLeast };
  }, [history]);

  if (!result)
    return (
      <Card>
        <Title>{t("bestDays.title")}</Title>
        <Empty>{t("bestDays.noActivity")}</Empty>
      </Card>
    );

  const { incomeBest, incomeWorst, spendingMost, spendingLeast } = result;

  return (
    <Card>
      <Title>{t("bestDays.title")}</Title>

      <Grid>
        {incomeBest && (
          <GridItem>
            <Emoji>💰</Emoji>
            <Label>{t("bestDays.earnedMost")}:</Label>
            <Day>{format(new Date(incomeBest.date), "MMM dd")}</Day>
            <Amount>€{incomeBest.amount.toFixed(2)}</Amount>
          </GridItem>
        )}

        {incomeWorst && (
          <GridItem>
            <Emoji>🪙</Emoji>
            <Label>{t("bestDays.earnedLeast")}:</Label>
            <Day>{format(new Date(incomeWorst.date), "MMM dd")}</Day>
            <Amount>€{incomeWorst.amount.toFixed(2)}</Amount>
          </GridItem>
        )}

        {spendingMost && (
          <GridItem>
            <Emoji>💸</Emoji>
            <Label>{t("bestDays.spentMost")}:</Label>
            <Day>{format(new Date(spendingMost.date), "MMM dd")}</Day>
            <Amount>€{spendingMost.amount.toFixed(2)}</Amount>
          </GridItem>
        )}

        {spendingLeast && (
          <GridItem>
            <Emoji>🧾</Emoji>
            <Label>{t("bestDays.spentLeast")}:</Label>
            <Day>{format(new Date(spendingLeast.date), "MMM dd")}</Day>
            <Amount>€{spendingLeast.amount.toFixed(2)}</Amount>
          </GridItem>
        )}
      </Grid>
    </Card>
  );
}

// Styled Components
const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  padding: 1.2em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  color: var(--fontPrimaryColor);
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin: 1em 0 2em 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2em;
`;

const GridItem = styled.div`
  background-color: var(--thirdBackgroundColor);
  padding: 1.2em;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Emoji = styled.div`
  font-size: 26px;
  margin-bottom: 6px;
`;

const Label = styled.div`
  font-size: 14px;
  opacity: 0.7;
`;

const Day = styled.div`
  padding: 6px 0;
  font-size: 16px;
  font-weight: 600;
`;

const Amount = styled.div`
  font-weight: bold;
  margin-top: 4px;
  font-size: 15px;
`;

const Empty = styled.div`
  opacity: 0.6;
  font-size: 14px;
  padding: 16px 0;
  text-align: center;
`;
